import React, { Suspense, lazy, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";

// Public-site chrome — kept eager because it appears on nearly every page.
import Header from "./components/Header";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";  // eager: first-paint route

// Every other public page → lazy. Product pages, help, blog, account, etc.
// These are code-split by webpack into their own chunks so a first-time
// visitor to /product/xxx only downloads ~200KB instead of the full 1.8MB app.
const CategoryPage        = lazy(() => import("./pages/CategoryPage"));
const ProductPage         = lazy(() => import("./pages/ProductPage"));
const ShopPage            = lazy(() => import("./pages/ShopPage"));
const CartPage            = lazy(() => import("./pages/CartPage"));
const PrivacyPolicy       = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService      = lazy(() => import("./pages/TermsOfService"));
const PromoPage           = lazy(() => import("./pages/PromoPage"));
const SendYourFilePage    = lazy(() => import("./pages/SendYourFilePage"));
const DigitalContactPage  = lazy(() => import("./pages/DigitalContactPage"));
const BlogListPage        = lazy(() => import("./pages/BlogListPage"));
const BlogArticlePage     = lazy(() => import("./pages/BlogArticlePage"));
const SignInPage          = lazy(() => import("./pages/SignInPage"));
const SignUpPage          = lazy(() => import("./pages/SignUpPage"));
const VerifyEmailPage     = lazy(() => import("./pages/VerifyEmailPage"));
const ForgotPasswordPage  = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage   = lazy(() => import("./pages/ResetPasswordPage"));
const AuthCallback        = lazy(() => import("./pages/AuthCallback"));
const AccountPage         = lazy(() => import("./pages/AccountPage"));
const CheckoutPage        = lazy(() => import("./pages/CheckoutPage"));
const OrderConfirmationPage = lazy(() => import("./pages/OrderConfirmationPage"));
const HelpPage            = lazy(() => import("./pages/HelpPage"));
const Free250Page         = lazy(() => import("./pages/Free250Page"));
const AboutUsPage         = lazy(() => import("./pages/AboutUsPage"));
const AioPackageLandingPage = lazy(() => import("./pages/AioPackageLandingPage"));
const AioCheckoutPage     = lazy(() => import("./pages/AioCheckoutPage"));
const ProofPage           = lazy(() => import("./pages/ProofPage"));
const PublicInvoicePage   = lazy(() => import("./pages/PublicInvoicePage"));

// Admin — always lazy. Public visitors NEVER download admin code this way.
import { AdminAuthProvider } from "./admin/AdminAuthContext";
const AdminLoginPage         = lazy(() => import("./admin/AdminLoginPage"));
const AdminProtected         = lazy(() => import("./admin/AdminProtected"));
const AdminBoardPage         = lazy(() => import("./admin/AdminBoardPage"));
const AdminDashboardPage     = lazy(() => import("./admin/AdminDashboardPage"));
const SalesPipelinePage      = lazy(() => import("./admin/SalesPipelinePage"));
const TeamMembersPage        = lazy(() => import("./admin/TeamMembersPage"));
const ContactsPage           = lazy(() => import("./admin/ContactsPage"));
const SalesCustomersListPage = lazy(() => import("./admin/sales/SalesCustomersListPage"));
const SalesCustomerDetailPage = lazy(() => import("./admin/sales/SalesCustomerDetailPage"));
const SalesInvoicesListPage  = lazy(() => import("./admin/sales/SalesInvoicesListPage"));
const SalesInvoicePreviewPage = lazy(() => import("./admin/sales/SalesInvoicePreviewPage"));
const SalesPaymentsListPage  = lazy(() => import("./admin/sales/SalesPaymentsListPage"));
const SalesWebOrdersListPage = lazy(() => import("./admin/sales/SalesWebOrdersListPage"));
const SalesLeadSourcesReportPage = lazy(() => import("./admin/sales/SalesLeadSourcesReportPage"));
const SalesPerformancePage = lazy(() => import("./admin/sales/SalesPerformancePage"));
const SalesSettingsPage      = lazy(() => import("./admin/sales/SalesSettingsPage"));
const SalesStubPage          = lazy(() => import("./admin/sales/SalesStubPage"));
const NewOrderPage           = lazy(() => import("./admin/NewOrderPage"));
const ManualOrderDetailPage  = lazy(() => import("./admin/ManualOrderDetailPage"));
const ReconciliationVaultPage = lazy(() => import("./admin/ReconciliationVaultPage"));
const ProcurementBoardPage   = lazy(() => import("./admin/ProcurementBoardPage"));
const CatalogAdminPage       = lazy(() => import("./admin/CatalogAdminPage"));
const InboxPage              = lazy(() => import("./admin/InboxPage"));
const CustomerUploadsPage    = lazy(() => import("./admin/CustomerUploadsPage"));
const CrmIssuesPage          = lazy(() => import("./admin/CrmIssuesPage"));
const ApprovalsPage          = lazy(() => import("./admin/ApprovalsPage"));
const VideoChatDashboardPage = lazy(() => import("./admin/VideoChatDashboardPage"));
const VideoCallRoomPage      = lazy(() => import("./admin/VideoCallRoomPage"));

// Send a GA4 page_view on every client-side route change (CRA single-page app).
// Wrapped in try/catch: if an ad-blocker or extension replaces window.gtag with
// something that throws, we must NOT let that unmount the whole React tree.
function GA4RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    try {
      if (typeof window === "undefined" || typeof window.gtag !== "function") return;
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    } catch { /* analytics must never break navigation */ }
  }, [location]);
  return null;
}
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "./components/ui/toaster";
import NavigationProgress from "./components/NavigationProgress";
import ErrorBoundary from "./components/ErrorBoundary";

function ScrollToTop() {
  const { pathname } = useLocation();
  // NOTE: block body is REQUIRED. A concise-body arrow like
  //   useEffect(() => window.scrollTo(...), [pathname])
  // implicitly returns whatever window.scrollTo() evaluates to. If that value
  // is not undefined and not a function, React treats it as a cleanup fn and
  // throws `TypeError: destroy is not a function` on the next effect teardown
  // (i.e. on every route change), which unmounts the whole app.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <ScrollToTop />
      <GA4RouteTracker />
      <main className="flex-1">{children}</main>
      <Footer />
      <Toaster />
    </div>
  );
}

/**
 * AppRouter — routes for the entire app.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Google OAuth uses @react-oauth/google in popup mode — the code is returned via
 * postMessage to the parent window, so no hash detection here is needed.
 */
function AppRouter() {
  const { pathname } = useLocation();
  // Customer-facing proof viewer — minimal chrome
  if (pathname.startsWith("/proof/")) {
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="/proof/:token" element={<ProofPage />} />
        </Routes>
      </>
    );
  }
  // Customer-facing hosted invoice — minimal chrome
  if (pathname.startsWith("/invoice/")) {
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="/invoice/:token" element={<PublicInvoicePage />} />
        </Routes>
      </>
    );
  }
  // Back-office runs without the customer Header/Footer chrome
  if (pathname.startsWith("/admin")) {
    return (
      <AdminAuthProvider>
        <ScrollToTop />
        <Routes>
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route
            path="/admin/dashboard"
            element={
              <AdminProtected>
                <AdminDashboardPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/board/:boardId"
            element={
              <AdminProtected>
                <AdminBoardPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/sales"
            element={
              <AdminProtected>
                <SalesPipelinePage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/team"
            element={
              <AdminProtected>
                <TeamMembersPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/contacts"
            element={
              <AdminProtected>
                <ContactsPage />
              </AdminProtected>
            }
          />
          {/* ── Sales book (Phase 1: Customers; stubs for Phase 2+3) ── */}
          <Route
            path="/admin/sales/customers"
            element={
              <AdminProtected>
                <SalesCustomersListPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/sales/customers/:contactId"
            element={
              <AdminProtected>
                <SalesCustomerDetailPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/sales/quotes"
            element={
              <AdminProtected>
                <SalesStubPage title="Quotes" subtitle="Create & track quotes" phase="Phase 3" />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/sales/invoices"
            element={
              <AdminProtected>
                <SalesInvoicesListPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/sales/invoices/:orderId"
            element={
              <AdminProtected>
                <SalesInvoicePreviewPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/sales/performance"
            element={
              <AdminProtected>
                <SalesPerformancePage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/sales/lead-sources"
            element={
              <AdminProtected>
                <SalesLeadSourcesReportPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/sales/web-orders"
            element={
              <AdminProtected>
                <SalesWebOrdersListPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/sales/payments"
            element={
              <AdminProtected>
                <SalesPaymentsListPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/sales/settings"
            element={
              <AdminProtected>
                <SalesSettingsPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/orders/new"
            element={
              <AdminProtected>
                <NewOrderPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/orders/:orderId"
            element={
              <AdminProtected>
                <ManualOrderDetailPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/reconciliation"
            element={
              <AdminProtected>
                <ReconciliationVaultPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/approvals"
            element={
              <AdminProtected>
                <ApprovalsPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/procurement"
            element={
              <AdminProtected>
                <ProcurementBoardPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/customer-uploads"
            element={
              <AdminProtected>
                <CustomerUploadsPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/crm-issues"
            element={
              <AdminProtected>
                <CrmIssuesPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/catalog"
            element={
              <AdminProtected>
                <CatalogAdminPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/inbox"
            element={
              <AdminProtected>
                <InboxPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/video"
            element={
              <AdminProtected>
                <VideoChatDashboardPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/video/room/:sessionId"
            element={
              <AdminProtected>
                <VideoCallRoomPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminProtected>
                <AdminDashboardPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/*"
            element={
              <AdminProtected>
                <AdminDashboardPage />
              </AdminProtected>
            }
          />
        </Routes>
      </AdminAuthProvider>
    );
  }
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/category/:slug" element={<CategoryPage />} />
        <Route path="/product/:slug" element={<ProductPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/order/:orderId" element={<OrderConfirmationPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/promo" element={<PromoPage />} />
        <Route path="/send-your-file" element={<SendYourFilePage />} />
        <Route path="/free250" element={<Free250Page />} />
        <Route path="/aio-package" element={<AioPackageLandingPage />} />
        <Route path="/aio-checkout" element={<AioCheckoutPage />} />
        <Route path="/contact" element={<DigitalContactPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/contact-us" element={<HelpPage />} />
        <Route path="/support" element={<HelpPage />} />
        <Route path="/about" element={<AboutUsPage />} />
        <Route path="/about-us" element={<AboutUsPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/contact-us" element={<HelpPage />} />
        <Route path="/support" element={<HelpPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogArticlePage />} />
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/sign-up" element={<SignUpPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/google" element={<AuthCallback />} />
        <Route path="/account/*" element={<AccountPage />} />
        <Route path="*" element={<HomePage />} />
      </Routes>
    </Layout>
  );
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

// Fallback shown while a lazy route chunk is downloading. Same visual
// treatment as the boot-loader in index.html so users don't perceive a jump.
function ChunkFallback() {
  // Same self-heal guard as index.html — if we're still on the fallback after
  // 10s a chunk is genuinely broken (stale bundle). Force a cache-busted reload.
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        const HEAL_KEY = "__lp_cache_healed_at";
        const now = Date.now();
        const last = parseInt(sessionStorage.getItem(HEAL_KEY) || "0", 10);
        if (now - last < 30000) return;
        sessionStorage.setItem(HEAL_KEY, String(now));
        const u = new URL(window.location.href);
        u.searchParams.set("_cb", String(now));
        window.location.replace(u.toString());
      } catch { /* noop */ }
    }, 10000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div style={{
      position: "fixed", inset: 0, display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", gap: 20,
      background: "linear-gradient(135deg,#F4F6FA 0%,#E7E9EE 100%)",
      color: "#0f172a", zIndex: 40,
    }}>
      <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: "-0.03em" }}>
        Let&apos;s <span style={{ color: "#2C66E3" }}>Print</span>
      </div>
      <div style={{
        width: 44, height: 44, borderRadius: "50%",
        border: "4px solid rgba(44,102,227,0.15)", borderTopColor: "#2C66E3",
        animation: "spin 700ms linear infinite",
      }} />
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}

// Reads the current pathname and passes it to <ErrorBoundary> so the boundary
// auto-resets when the URL changes (navigating away from a crashing route
// heals the app without a hard refresh).
function RouterErrorBoundary({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary pathname={pathname}>{children}</ErrorBoundary>;
}

function App() {
  const tree = (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <ScrollToTop />
          <NavigationProgress />
          <RouterErrorBoundary>
            <Suspense fallback={<ChunkFallback />}>
              <AppRouter />
            </Suspense>
          </RouterErrorBoundary>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
  return (
    <div className="App">
      {GOOGLE_CLIENT_ID ? (
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{tree}</GoogleOAuthProvider>
      ) : (
        tree
      )}
    </div>
  );
}

export default App;
